import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface FAQ2ItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}
function FAQ2Item({
  question,
  answer,
  isOpen,
  onToggle
}: FAQ2ItemProps) {
  return <motion.div className="bg-white border border-gray-300 rounded-xl overflow-hidden" initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.2
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} data-id="element-164">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green/50" aria-expanded={isOpen} data-id="element-165">
        <span className="text-base md:text-lg font-semibold text-gray-900 pr-4" data-id="element-166">
          {question}
        </span>
        {/* Toggle switch */}
        <div className={`relative w-12 h-6 rounded-full flex-shrink-0 transition-colors duration-300 ${isOpen ? 'bg-brand-green' : 'bg-gray-300'}`} data-id="element-167">
          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 ${isOpen ? 'translate-x-6' : 'translate-x-0.5'}`} data-id="element-168" />
        </div>
      </button>
      <AnimatePresence initial={false} data-id="element-169">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="overflow-hidden" data-id="element-170">
            <div className="px-5 pb-5 text-gray-700 leading-relaxed text-sm md:text-base" data-id="element-171">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function FAQ2Section() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="w-full bg-brand-bg pt-6 md:pt-8 pb-16 md:pb-20 px-6" data-id="element-172">
      <div className="max-w-3xl mx-auto" data-id="element-173">
        <motion.div className="relative text-center mb-10" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-174">
          <div className="absolute left-1/2 -translate-x-1/2 top-[-20%] w-[800px] h-[600px] bg-[#000000]/[0.05] rounded-full blur-[120px] pointer-events-none" data-id="element-175" />
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-tight relative z-10" data-id="element-176">
            Preguntas <span className="text-[#000000]" data-id="element-177">frecuentes</span>
          </h2>
        </motion.div>

        <div className="flex flex-col gap-4" data-id="element-178">
          <FAQ2Item question="¿Para quién es Emprende Digital?" answer="Para cualquier persona que quiera generar ingresos online desde cero, incluso si nunca vendiste nada. Si hoy no sabés por dónde empezar, Emprende Digital te da un camino claro." isOpen={openIndex === 0} onToggle={() => handleToggle(0)} data-id="element-179" />
          <FAQ2Item question="¿Cuánto dura el curso?" answer="Según tu dedicación, no tiene duración fija. Avanzás a tu ritmo. Tenemos alumnos que en pocos días ya estaban aplicando y generando sus primeras ventas." isOpen={openIndex === 1} onToggle={() => handleToggle(1)} data-id="element-180" />
          <FAQ2Item question="¿Necesito experiencia previa?" answer="No. Está diseñado para empezar desde cero, paso a paso, sin conocimientos técnicos ni experiencia previa." isOpen={openIndex === 2} onToggle={() => handleToggle(2)} data-id="element-181" />
          <FAQ2Item question="¿Qué incluye exactamente?" answer={<div data-id="element-183">
                <p className="font-semibold text-gray-900 mb-2" data-id="element-184">
                  📘 Curso Completo Paso a Paso — $19.900
                </p>
                <ul className="space-y-1.5 mb-4" data-id="element-185">
                  <li className="flex items-start gap-2" data-id="element-186">
                    <span className="text-brand-green mt-0.5" data-id="element-187">🟢</span>
                    <span data-id="element-188">Acceso al curso completo paso a paso</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-189">
                    <span className="text-brand-green mt-0.5" data-id="element-190">🟢</span>
                    <span data-id="element-191">
                      Llamada grupal donde respondemos todas tus dudas
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-192">
                    <span className="text-brand-green mt-0.5" data-id="element-193">🟢</span>
                    <span data-id="element-194">Comunidad de WhatsApp</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-195">
                    <span className="text-brand-green mt-0.5" data-id="element-196">🎁</span>
                    <span data-id="element-197">
                      3 bonos valorados en $25.000 totalmente{' '}
                      <strong className="text-brand-green" data-id="element-198">¡GRATIS!</strong>
                    </span>
                  </li>
                </ul>
                <p className="font-semibold text-gray-900 mb-2" data-id="element-199">
                  🚀 Membresía — $49.900
                </p>
                <ul className="space-y-1.5" data-id="element-200">
                  <li className="flex items-start gap-2" data-id="element-201">
                    <span className="text-brand-green mt-0.5" data-id="element-202">🟢</span>
                    <span data-id="element-203">Acceso al curso completo paso a paso</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-204">
                    <span className="text-brand-green mt-0.5" data-id="element-205">🟢</span>
                    <span data-id="element-206">Mentorías en vivo 1 vez por semana</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-207">
                    <span className="text-brand-green mt-0.5" data-id="element-208">🟢</span>
                    <span data-id="element-209">Seguimiento semana a semana</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-210">
                    <span className="text-brand-green mt-0.5" data-id="element-211">🟢</span>
                    <span data-id="element-212">Comunidad VIP de WhatsApp</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-213">
                    <span className="text-brand-green mt-0.5" data-id="element-214">🎁</span>
                    <span data-id="element-215">
                      3 bonos valorados en $25.000 totalmente{' '}
                      <strong className="text-brand-green" data-id="element-216">¡GRATIS!</strong>
                    </span>
                  </li>
                </ul>
              </div>} isOpen={openIndex === 3} onToggle={() => handleToggle(3)} data-id="element-182" />
          <FAQ2Item question="¿Cómo accedo después de pagar?" answer="Acceso inmediato. Apenas realizás el pago, recibís todo por email para empezar al instante." isOpen={openIndex === 4} onToggle={() => handleToggle(4)} data-id="element-217" />
          <FAQ2Item question="¿Cuánto tiempo debo dedicar por día?" answer="Con 45–60 minutos diarios ya podés avanzar. Si le dedicás más tiempo, los resultados llegan más rápido." isOpen={openIndex === 5} onToggle={() => handleToggle(5)} data-id="element-218" />
        </div>

        {/* CTA Button */}
      </div>
    </section>;
}